import { useState, useEffect, useCallback, useRef } from "react";
import {
  getAdminUsers,
  getAdminUserDetail,
  getAdminUserTransactions,
  suspendUser,
  unsuspendUser,
  adjustUserBalance,
  grantAdminBonus,
  deleteAdminUser,
} from "../../../api/apiServices";

// ── Helpers ───────────────────────────────────────────────────
function fmtDate(d, opts) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NG", opts || { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-NG", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtAmt(n) { return Number(n || 0).toLocaleString("en-NG"); }
function initials(name = "", email = "") {
  const src = name || email;
  return src.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}
function shortDevice(ua = "") {
  if (!ua) return "—";
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad/i.test(ua);
  const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/);
  const browser = browserMatch ? browserMatch[1] : "Browser";
  return `${isAndroid ? "Android" : isIOS ? "iOS" : "Desktop"} · ${browser}`;
}

const KYC_META = {
  approved:       { label: "KYC",          color: "#059669", bg: "#ECFDF5" },
  submitted:      { label: "Pending KYC",  color: "#D97706", bg: "#FFFBEB" },
  under_review:   { label: "In Review",    color: "#0284C7", bg: "#EFF6FF" },
  rejected:       { label: "KYC Rejected", color: "#DC2626", bg: "#FEF2F2" },
  info_requested: { label: "Info Needed",  color: "#7C3AED", bg: "#F5F3FF" },
  not_started:    { label: "No KYC",       color: "#64748B", bg: "#F8FAFC" },
};

const TX_META = {
  buy:              { ri: "ri-arrow-down-line",         color: "#059669", bg: "#ECFDF5" },
  sell:             { ri: "ri-arrow-up-line",           color: "#DC2626", bg: "#FEF2F2" },
  deposit:          { ri: "ri-arrow-down-circle-line",  color: "#059669", bg: "#ECFDF5" },
  withdrawal:       { ri: "ri-arrow-up-circle-line",    color: "#D97706", bg: "#FFFBEB" },
  withdraw_crypto:  { ri: "ri-arrow-up-circle-line",    color: "#D97706", bg: "#FFFBEB" },
  reward_withdrawal:{ ri: "ri-gift-line",               color: "#7C3AED", bg: "#F5F3FF" },
  bill_payment:     { ri: "ri-receipt-line",            color: "#7C3AED", bg: "#F5F3FF" },
  admin_adjustment: { ri: "ri-settings-3-line",         color: "#64748B", bg: "#F8FAFC" },
};
function getTxMeta(type) {
  return TX_META[type] || { ri: "ri-exchange-line", color: "#64748B", bg: "#F8FAFC" };
}

// Reward event type → icon/color (all these are credits in the actual data —
// there's no debit type coming back, so we style by category, not sign)
const REWARD_META = {
  referral_first_trade: { ri: "ri-team-line",       color: "#7C3AED", bg: "#F5F3FF" },
  cashback_bill:        { ri: "ri-percent-line",    color: "#059669", bg: "#ECFDF5" },
  admin_bonus:          { ri: "ri-gift-line",       color: "#D97706", bg: "#FFFBEB" },
};
function getRewardMeta(type) {
  return REWARD_META[type] || { ri: "ri-coin-line", color: "#64748B", bg: "#F8FAFC" };
}

function Row({ label, value, mono, accent }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between items-start px-4 py-3 border-b border-white/4 last:border-0">
      <span className="text-white/40 text-sm flex-shrink-0 mr-4">{label}</span>
      <span className={`text-sm font-medium text-right break-all ${mono ? "font-mono text-xs" : ""}`}
        style={{ color: accent || "rgba(255,255,255,0.85)" }}>
        {value}
      </span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <p className="text-[10px] font-bold text-white/25 uppercase tracking-widest px-4 py-3 border-b border-white/5">
      {children}
    </p>
  );
}

function Pill({ children, color, bg }) {
  return (
    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: bg, color }}>
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// User Detail Panel
// ─────────────────────────────────────────────────────────────
function UserDetailPanel({ userId, onClose, onAction }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [txPage, setTxPage]       = useState(1);
  const [txData, setTxData]       = useState([]);
  const [txTotal, setTxTotal]     = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [actionLoading, setActionLoading] = useState(null);

  const [suspendReason, setSuspendReason] = useState("");
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustType, setAdjustType]     = useState("credit");
  const [adjustNote, setAdjustNote]     = useState("");
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [bonusAmount, setBonusAmount]   = useState("");
  const [bonusDesc, setBonusDesc]       = useState("");
  const [showBonusForm, setShowBonusForm] = useState(false);
  const [actionMsg, setActionMsg]       = useState(null);

  // Delete flow — requires typing the username to confirm
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminUserDetail(userId);
      if (res.status) setData(res.data);
    } catch {}
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const loadTxns = useCallback(async (page = 1) => {
    setTxLoading(true);
    try {
      const res = await getAdminUserTransactions(userId, page);
      if (res.status) {
        setTxData(prev => page === 1 ? res.data : [...prev, ...res.data]);
        setTxTotal(res.pagination.total);
        setTxPage(page);
      }
    } catch {}
    finally { setTxLoading(false); }
  }, [userId]);

  useEffect(() => {
    if (activeTab === "history" && txData.length === 0) loadTxns(1);
  }, [activeTab]);

  const handleSuspend = async () => {
    setActionLoading("suspend");
    try {
      await suspendUser(userId, suspendReason);
      setActionMsg({ type: "success", text: "User suspended" });
      setShowSuspendForm(false);
      onAction();
      setData(d => ({ ...d, user: { ...d.user, suspended: true }, security: { ...d.security, suspended: true } }));
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.message || "Failed" });
    } finally { setActionLoading(null); }
  };

  const handleUnsuspend = async () => {
    setActionLoading("unsuspend");
    try {
      await unsuspendUser(userId);
      setActionMsg({ type: "success", text: "User reinstated" });
      onAction();
      setData(d => ({ ...d, user: { ...d.user, suspended: false }, security: { ...d.security, suspended: false } }));
    } catch { setActionMsg({ type: "error", text: "Failed" }); }
    finally { setActionLoading(null); }
  };

  const handleAdjust = async () => {
    if (!adjustAmount || isNaN(Number(adjustAmount))) return;
    setActionLoading("adjust");
    try {
      const res = await adjustUserBalance(userId, Number(adjustAmount), adjustType, adjustNote);
      setActionMsg({ type: "success", text: res.message || "Balance adjusted" });
      setShowAdjustForm(false); setAdjustAmount(""); setAdjustNote("");
      const updated = await getAdminUserDetail(userId);
      if (updated.status) setData(updated.data);
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.message || "Failed" });
    } finally { setActionLoading(null); }
  };

  const handleBonus = async () => {
    if (!bonusAmount || Number(bonusAmount) < 1) return;
    setActionLoading("bonus");
    try {
      const res = await grantAdminBonus(userId, Number(bonusAmount), bonusDesc || "Admin bonus");
      setActionMsg({ type: "success", text: res.message || "Bonus credited" });
      setShowBonusForm(false); setBonusAmount(""); setBonusDesc("");
      const updated = await getAdminUserDetail(userId);
      if (updated.status) setData(updated.data);
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.message || "Failed" });
    } finally { setActionLoading(null); }
  };

  const handleDelete = async () => {
    setActionLoading("delete");
    try {
      await deleteAdminUser(userId);
      onAction();   // refresh the list
      onClose();    // close the panel — the user no longer exists
    } catch (err) {
      setActionMsg({ type: "error", text: err?.response?.data?.message || "Failed to delete user" });
      setActionLoading(null);
    }
  };

  const user     = data?.user     || {};
  const wallet   = data?.wallet   || {};
  const kyc      = data?.kyc      || {};
  const rewards  = data?.rewards  || {};
  const security = data?.security || {};
  const txStats  = data?.txStats  || {};
  const kycMeta  = KYC_META[kyc.status || "not_started"];

  // Coins with a nonzero balance, flattened out of wallet.networks[].tokens[]
  const cryptoHoldings = (wallet.networks || [])
    .flatMap(net => (net.tokens || []).map(t => ({ ...t, network: net.network, address: net.address })))
    .filter(t => Number(t.balance) > 0);

  const confirmTextMatches = deleteConfirmText.trim().toLowerCase() === (user.userName || "").toLowerCase()
    && deleteConfirmText.length > 0;

  const TABS = [
    { id: "profile",  label: "Profile",  icon: "ri-user-3-line" },
    { id: "security", label: "Security", icon: "ri-shield-keyhole-line" },
    { id: "kyc",      label: "KYC",      icon: "ri-id-card-line" },
    { id: "history",  label: "History",  icon: "ri-exchange-line" },
    { id: "actions",  label: "Actions",  icon: "ri-tools-line" },
  ];

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#0f0f0f] flex flex-col shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between flex-shrink-0">
          {loading ? (
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 rounded-full bg-white/10 animate-pulse" />
              <div className="space-y-2"><div className="h-3 w-32 bg-white/10 rounded animate-pulse" /><div className="h-2.5 w-24 bg-white/10 rounded animate-pulse" /></div>
            </div>
          ) : (
            <div className="flex items-center gap-4 min-w-0">
              {user.profileImage ? (
                <img src={user.profileImage} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#00C896]/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#00C896] font-bold text-sm">{initials(user.userName, user.email)}</span>
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-bold truncate">{user.userName || "—"}</p>
                  {user.suspended && <Pill color="#DC2626" bg="rgba(220,38,38,0.15)">Suspended</Pill>}
                  <Pill color={kycMeta.color} bg={kycMeta.bg}>{kycMeta.label}</Pill>
                  {user.twoFactorEnabled && <Pill color="#0284C7" bg="#EFF6FF">2FA</Pill>}
                  {user.riskScore > 0 && <Pill color="#D97706" bg="#FFFBEB">Risk {user.riskScore}</Pill>}
                </div>
                <p className="text-white/40 text-xs truncate">{user.email} · <span className="font-mono">{user.userId}</span></p>
              </div>
            </div>
          )}
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors flex-shrink-0 ml-3">
            <i className="ri-close-line text-white/60" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/5 overflow-x-auto flex-shrink-0">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-max flex items-center justify-center gap-1 px-3 py-3 text-[11px] font-semibold transition-all border-b-2 ${
                activeTab === tab.id ? "text-[#00C896] border-[#00C896]" : "text-white/30 border-transparent hover:text-white/60"
              }`}>
              <i className={tab.icon} />{tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(6)].map((_, i) => <div key={i} className="h-12 bg-white/5 rounded-xl animate-pulse" />)}
            </div>
          ) : (

            // ── PROFILE TAB ───────────────────────────────────
            activeTab === "profile" ? (
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Naira Balance", value: `₦${fmtAmt(wallet.nairaBalance)}`, icon: "ri-money-dollar-circle-line", color: "#059669" },
                    { label: "Reward Wallet", value: `₦${fmtAmt(rewards.rewardBalance)}`, icon: "ri-gift-line", color: "#7C3AED" },
                    { label: "Day Streak", value: `${rewards.currentStreak || 0} 🔥`, icon: "ri-fire-line", color: "#F97316" },
                  ].map(s => (
                    <div key={s.label} className="bg-white/4 rounded-xl p-3 text-center border border-white/5">
                      <i className={`${s.icon} text-base`} style={{ color: s.color }} />
                      <p className="text-white font-bold text-sm mt-1">{s.value}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>

                {txStats.totalCount > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Transaction Summary</SectionTitle>
                    <Row label="Total Transactions" value={txStats.totalCount?.toLocaleString()} />
                    <Row label="Total Volume" value={`₦${fmtAmt(txStats.totalVolume)}`} accent="#00C896" />
                    {Object.entries(txStats.byType || {}).map(([type, s]) => (
                      <Row key={type} label={type.replace(/_/g, " ")} value={`${s.count}x · ₦${fmtAmt(s.volume)}`} />
                    ))}
                  </div>
                )}

                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <SectionTitle>Account Info</SectionTitle>
                  <Row label="User ID"       value={user.userId} mono />
                  <Row label="Email"         value={user.email} />
                  <Row label="Email Verified" value={user.emailVerified ? "Yes" : "No"} accent={user.emailVerified ? "#059669" : "#DC2626"} />
                  <Row label="Username"      value={user.userName} />
                  <Row label="Phone"         value={user.phone} />
                  <Row label="Role"          value={user.role} />
                  <Row label="Auth Provider" value={user.authProvider || "email"} />
                  <Row label="Referral Code" value={user.referralCode} mono />
                  <Row label="Signup Source" value={user.signupSource} />
                  <Row label="Terms Agreed"  value={user.agreedToTerms ? "Yes" : "No"} accent={user.agreedToTerms ? "#059669" : "#D97706"} />
                  <Row label="Joined"        value={fmtDateTime(user.createdAt)} />
                  <Row label="Last Updated"  value={fmtDateTime(user.updatedAt)} />
                </div>

                {/* Saved bank accounts */}
                {user.savedBanks?.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Saved Banks ({user.savedBanks.length})</SectionTitle>
                    {user.savedBanks.map(b => (
                      <div key={b._id} className="px-4 py-3 border-b border-white/4 last:border-0">
                        <div className="flex justify-between items-center">
                          <p className="text-white text-sm">{b.bankName}</p>
                          <span className="font-mono text-xs text-white/40">{b.accountNumber}</span>
                        </div>
                        <p className="text-white/30 text-xs">{b.accountName}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Crypto holdings — flattened from wallet.networks[].tokens[] */}
                {cryptoHoldings.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Crypto Holdings</SectionTitle>
                    {cryptoHoldings.map((t, i) => (
                      <Row key={`${t.network}-${t.currency}-${i}`} label={`${t.currency} (${t.network})`} value={t.balance} />
                    ))}
                  </div>
                )}

                {data?.savings?.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Savings Goals ({data.savings.length})</SectionTitle>
                    {data.savings.map(g => (
                      <div key={g._id} className="px-4 py-3 border-b border-white/4 last:border-0">
                        <div className="flex justify-between items-center mb-1">
                          <p className="text-white text-sm font-medium">{g.title}</p>
                          <Pill color={g.status === "active" ? "#059669" : "#64748B"} bg={g.status === "active" ? "#ECFDF5" : "#F8FAFC"}>{g.status}</Pill>
                        </div>
                        <p className="text-white/40 text-xs">
                          ₦{fmtAmt(g.currentAmount)} / ₦{fmtAmt(g.targetAmount)} · {g.progressPercent || 0}% · {g.type}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {data?.companies?.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Company Memberships ({data.companies.length})</SectionTitle>
                    {data.companies.map(m => (
                      <div key={m.companyId} className="px-4 py-3 border-b border-white/4 last:border-0 flex justify-between items-center">
                        <span className="text-white/60 text-sm font-mono text-xs">{m.companyId}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-white/40 text-xs capitalize">{m.role}</span>
                          <Pill color={m.status === "active" ? "#059669" : "#64748B"} bg={m.status === "active" ? "#ECFDF5" : "#F8FAFC"}>{m.status}</Pill>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {data?.paymentLinks?.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Payment Links ({data.paymentLinks.length})</SectionTitle>
                    {data.paymentLinks.map(l => (
                      <div key={l._id} className="px-4 py-3 border-b border-white/4 last:border-0">
                        <div className="flex justify-between items-center mb-0.5">
                          <p className="text-white text-sm">{l.title}</p>
                          <Pill color={l.status === "active" ? "#059669" : "#64748B"} bg={l.status === "active" ? "#ECFDF5" : "#F8FAFC"}>{l.status}</Pill>
                        </div>
                        <p className="text-white/30 text-xs">
                          {l.amount ? `₦${fmtAmt(l.amount)}` : "Open amount"} · Received ₦{fmtAmt(l.totalReceived)} · {l.paymentCount || 0} payments
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {rewards.milestones && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Reward Milestones</SectionTitle>
                    <div className="px-4 py-3 flex flex-wrap gap-2">
                      {Object.entries(rewards.milestones).filter(([, v]) => v).map(([key]) => (
                        <span key={key} className="text-[10px] bg-[#00C896]/10 text-[#00C896] px-2.5 py-1 rounded-full font-semibold">
                          ✓ {key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}
                        </span>
                      ))}
                      {Object.values(rewards.milestones).every(v => !v) && (
                        <p className="text-white/25 text-xs">No milestones completed</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Reward history — styled by event type, all amounts are credits */}
                {data?.rewardHistory?.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Reward History (last {data.rewardHistory.length})</SectionTitle>
                    {data.rewardHistory.map((r, i) => {
                      const meta = getRewardMeta(r.type);
                      return (
                        <div key={r._id || i} className="flex items-center gap-3 px-4 py-3 border-b border-white/4 last:border-0">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: meta.bg }}>
                            <i className={`${meta.ri} text-sm`} style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white/70 text-sm">{r.description || r.type}</p>
                            <p className="text-white/25 text-xs">{fmtDateTime(r.createdAt)}</p>
                          </div>
                          <span className="font-bold text-sm flex-shrink-0" style={{ color: meta.color }}>
                            +₦{fmtAmt(r.amount)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )

            // ── SECURITY TAB ──────────────────────────────────
            : activeTab === "security" ? (
              <div className="p-5 space-y-4">
                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <SectionTitle>Account Status</SectionTitle>
                  <Row label="Status" value={security.suspended ? "Suspended" : "Active"}
                    accent={security.suspended ? "#DC2626" : "#059669"} />
                  {security.suspended && (
                    <>
                      <Row label="Suspended At"  value={fmtDateTime(security.suspendedAt)} />
                      <Row label="Suspended By"  value={security.suspendedBy} mono />
                      <Row label="Reason"        value={security.suspendedReason || "—"} />
                    </>
                  )}
                </div>

                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <SectionTitle>Authentication</SectionTitle>
                  <Row label="Two-Factor Auth" value={user.twoFactorEnabled ? `Enabled (${user.twoFactorMethod || "—"})` : "Disabled"}
                    accent={user.twoFactorEnabled ? "#059669" : "#64748B"} />
                  <Row label="Has Transaction PIN" value={user.hasTransactionPin ? "Yes" : "No"} />
                  <Row label="Failed Login Attempts" value={user.loginAttempts || 0} />
                  <Row label="Password Changed" value={fmtDateTime(user.passwordChangedAt)} />
                </div>

                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <SectionTitle>Risk</SectionTitle>
                  <Row label="Risk Score" value={user.riskScore ?? 0} accent={user.riskScore > 0 ? "#D97706" : undefined} />
                  {user.riskFlags?.length > 0 ? (
                    <div className="px-4 py-3 flex flex-wrap gap-2">
                      {user.riskFlags.map((f, i) => <Pill key={i} color="#DC2626" bg="#FEF2F2">{f}</Pill>)}
                    </div>
                  ) : (
                    <Row label="Flags" value="None" />
                  )}
                </div>

                <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                  <SectionTitle>Last Login</SectionTitle>
                  <Row label="Time"       value={fmtDateTime(security.lastLoginAt)} />
                  <Row label="IP Address" value={security.lastLoginIp} mono />
                  {user.lastLoginDevice && <Row label="Device" value={shortDevice(user.lastLoginDevice)} />}
                </div>

                {security.loginHistory?.length > 0 ? (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Login History (last {security.loginHistory.length})</SectionTitle>
                    {security.loginHistory.map((entry, i) => (
                      <div key={i} className="px-4 py-3 border-b border-white/4 last:border-0">
                        <div className="flex justify-between items-center mb-0.5">
                          <span className="font-mono text-xs text-white/70">{entry.ip || "—"}</span>
                          <span className="text-white/30 text-xs">{fmtDateTime(entry.at)}</span>
                        </div>
                        {entry.device && <p className="text-white/25 text-[11px] truncate">{shortDevice(entry.device)}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white/4 rounded-xl border border-white/5 p-5 text-center">
                    <i className="ri-history-line text-white/15 text-2xl mb-2" />
                    <p className="text-white/25 text-sm">No login history stored yet</p>
                  </div>
                )}
              </div>
            )

            // ── KYC TAB ───────────────────────────────────────
            : activeTab === "kyc" ? (
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ background: kycMeta.bg + "15", borderColor: kycMeta.bg }}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: kycMeta.bg }}>
                    <i className="ri-id-card-line" style={{ color: kycMeta.color }} />
                  </div>
                  <div>
                    <p className="font-bold text-sm" style={{ color: kycMeta.color }}>{kycMeta.label}</p>
                    {kyc.submittedAt && <p className="text-white/40 text-xs">Submitted {fmtDateTime(kyc.submittedAt)}</p>}
                    {kyc.approvedAt  && <p className="text-white/40 text-xs">Approved {fmtDateTime(kyc.approvedAt)}</p>}
                  </div>
                </div>

                {kyc.rejectionReason && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                    <p className="text-[10px] font-bold text-red-400/70 uppercase tracking-wide mb-1">Rejection Reason</p>
                    <p className="text-red-300 text-sm">{kyc.rejectionReason}</p>
                  </div>
                )}

                {kyc.personalInfo && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Personal Information</SectionTitle>
                    <Row label="First Name" value={kyc.personalInfo.firstName} />
                    <Row label="Last Name"  value={kyc.personalInfo.lastName} />
                    <Row label="DOB"        value={fmtDate(kyc.personalInfo.dateOfBirth)} />
                    <Row label="Phone"      value={kyc.personalInfo.phone} />
                    <Row label="Address"    value={kyc.personalInfo.address} />
                    <Row label="City"       value={kyc.personalInfo.city} />
                    <Row label="State"      value={kyc.personalInfo.state} />
                    <Row label="Country"    value={kyc.personalInfo.country} />
                    <Row label="Nationality" value={kyc.personalInfo.nationality} />
                  </div>
                )}

                {kyc.idDocument && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>ID Document</SectionTitle>
                    <Row label="Type"      value={kyc.idDocument.type} />
                    <Row label="ID Number" value={kyc.idDocument.idNumber} mono />
                    <Row label="Issued"    value={fmtDate(kyc.idDocument.issuedAt)} />
                    <Row label="Expires"   value={fmtDate(kyc.idDocument.expiresAt)} />
                    <Row label="Country"   value={kyc.idDocument.issuingCountry} />
                    {kyc.idDocument.frontUrl && (
                      <div className="px-4 py-3 border-b border-white/4">
                        <p className="text-white/30 text-xs mb-2">Front</p>
                        <a href={kyc.idDocument.frontUrl} target="_blank" rel="noreferrer"
                          className="block rounded-xl overflow-hidden border border-white/10 hover:border-[#00C896]/30 transition-colors">
                          <img src={kyc.idDocument.frontUrl} alt="ID Front" className="w-full max-h-48 object-cover" />
                        </a>
                      </div>
                    )}
                    {kyc.idDocument.backUrl && (
                      <div className="px-4 py-3 border-b border-white/4">
                        <p className="text-white/30 text-xs mb-2">Back</p>
                        <a href={kyc.idDocument.backUrl} target="_blank" rel="noreferrer"
                          className="block rounded-xl overflow-hidden border border-white/10 hover:border-[#00C896]/30 transition-colors">
                          <img src={kyc.idDocument.backUrl} alt="ID Back" className="w-full max-h-48 object-cover" />
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {kyc.selfie && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>Selfie</SectionTitle>
                    <div className="p-3">
                      <a href={kyc.selfie} target="_blank" rel="noreferrer"
                        className="block rounded-xl overflow-hidden border border-white/10 hover:border-[#00C896]/30 transition-colors">
                        <img src={kyc.selfie} alt="Selfie" className="w-full max-h-64 object-cover" />
                      </a>
                    </div>
                  </div>
                )}

                {kyc.history?.length > 0 && (
                  <div className="bg-white/4 rounded-xl border border-white/5 overflow-hidden">
                    <SectionTitle>KYC History</SectionTitle>
                    {kyc.history.map((h, i) => (
                      <div key={i} className="px-4 py-3 border-b border-white/4 last:border-0">
                        <div className="flex justify-between items-center mb-0.5">
                          <span className="text-white/70 text-sm capitalize">{h.status}</span>
                          <span className="text-white/30 text-xs">{fmtDateTime(h.at || h.changedAt)}</span>
                        </div>
                        {h.reason && <p className="text-white/30 text-xs">{h.reason}</p>}
                        {h.by     && <p className="text-white/20 text-[10px]">By: {h.by}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {!kyc.personalInfo && !kyc.idDocument && kyc.status === "not_started" && (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <i className="ri-id-card-line text-white/10 text-4xl mb-2" />
                    <p className="text-white/25 text-sm">No KYC submitted yet</p>
                  </div>
                )}
              </div>
            )

            // ── HISTORY TAB ───────────────────────────────────
            : activeTab === "history" ? (
              <div className="p-4 space-y-3">
                {txStats.totalCount > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/4 rounded-xl p-3 text-center border border-white/5">
                      <p className="text-white font-bold">{fmtAmt(txStats.totalCount)}</p>
                      <p className="text-white/30 text-[10px]">Total Transactions</p>
                    </div>
                    <div className="bg-white/4 rounded-xl p-3 text-center border border-white/5">
                      <p className="text-white font-bold">₦{fmtAmt(txStats.totalVolume)}</p>
                      <p className="text-white/30 text-[10px]">Total Volume</p>
                    </div>
                  </div>
                )}

                <p className="text-white/30 text-xs px-1">{txTotal} transactions total</p>

                {txLoading && txData.length === 0 ? (
                  [...Array(5)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white/4 rounded-xl p-3 border border-white/5">
                      <div className="w-9 h-9 rounded-xl bg-white/10 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-1.5"><div className="h-3 w-28 bg-white/10 rounded animate-pulse" /><div className="h-2.5 w-20 bg-white/10 rounded animate-pulse" /></div>
                      <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
                    </div>
                  ))
                ) : txData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <i className="ri-file-list-3-line text-white/15 text-4xl mb-2" />
                    <p className="text-white/25 text-sm">No transactions</p>
                  </div>
                ) : (
                  <>
                    {txData.map(tx => {
                      const meta = getTxMeta(tx.type);
                      return (
                        <div key={tx._id} className="flex items-center gap-3 bg-white/4 rounded-xl p-3 border border-white/5">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: meta.bg + "20" }}>
                            <i className={`${meta.ri} text-sm`} style={{ color: meta.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">
                              {tx.type === "bill_payment" ? (tx.assetName || tx.asset) : tx.type.replace(/_/g, " ")}
                            </p>
                            <p className="text-white/30 text-xs">{fmtDateTime(tx.createdAt)}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-white text-sm font-bold">₦{fmtAmt(tx.nairaAmount)}</p>
                            <span className={`text-[10px] font-semibold ${
                              tx.status === "completed" ? "text-[#00C896]"
                              : tx.status === "pending" || tx.status === "processing" ? "text-amber-400"
                              : "text-red-400"
                            }`}>{tx.status}</span>
                          </div>
                        </div>
                      );
                    })}
                    {txData.length < txTotal && (
                      <button onClick={() => loadTxns(txPage + 1)} disabled={txLoading}
                        className="w-full py-3 text-xs font-semibold text-[#00C896] hover:text-[#00b386] transition-colors disabled:opacity-50 text-center">
                        {txLoading ? "Loading…" : `Load more (${txTotal - txData.length} remaining)`}
                      </button>
                    )}
                  </>
                )}
              </div>
            )

            // ── ACTIONS TAB ───────────────────────────────────
            : (
              <div className="p-6 space-y-4">
                {actionMsg && (
                  <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-medium ${
                    actionMsg.type === "success" ? "bg-[#00C896]/10 border border-[#00C896]/20 text-[#00C896]" : "bg-red-500/10 border border-red-500/20 text-red-400"
                  }`}>
                    <i className={actionMsg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                    {actionMsg.text}
                    <button onClick={() => setActionMsg(null)} className="ml-auto opacity-60 hover:opacity-100"><i className="ri-close-line text-xs" /></button>
                  </div>
                )}

                {/* Suspend / Unsuspend */}
                <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-semibold">Account Status</p>
                      <p className="text-white/30 text-xs mt-0.5">
                        {user.suspended ? `Suspended ${fmtDate(security.suspendedAt)}` : "Account is active"}
                      </p>
                    </div>
                    <Pill color={user.suspended ? "#DC2626" : "#00C896"} bg={user.suspended ? "rgba(220,38,38,0.2)" : "rgba(0,200,150,0.15)"}>
                      {user.suspended ? "Suspended" : "Active"}
                    </Pill>
                  </div>
                  {user.suspended ? (
                    <button onClick={handleUnsuspend} disabled={!!actionLoading}
                      className="w-full py-2.5 rounded-xl bg-[#00C896]/15 text-[#00C896] text-sm font-bold hover:bg-[#00C896]/25 transition-colors disabled:opacity-50">
                      {actionLoading === "unsuspend" ? "Reinstating…" : "Reinstate Account"}
                    </button>
                  ) : (
                    <>
                      <button onClick={() => { setShowSuspendForm(s => !s); setShowAdjustForm(false); setShowBonusForm(false); setShowDeleteForm(false); }}
                        className="w-full py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-bold border border-red-500/20 hover:bg-red-500/20 transition-colors">
                        Suspend Account
                      </button>
                      {showSuspendForm && (
                        <div className="space-y-2">
                          <textarea value={suspendReason} onChange={e => setSuspendReason(e.target.value)}
                            placeholder="Reason for suspension (shown to user)…" rows={3}
                            className="w-full bg-[#161616] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-red-400/50 resize-none" />
                          <button onClick={handleSuspend} disabled={!!actionLoading}
                            className="w-full py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-50">
                            {actionLoading === "suspend" ? "Suspending…" : "Confirm Suspension"}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Balance adjustment */}
                <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
                  <div>
                    <p className="text-white text-sm font-semibold">Naira Balance Adjustment</p>
                    <p className="text-white/30 text-xs mt-0.5">Current: ₦{fmtAmt(wallet.nairaBalance)}</p>
                  </div>
                  <button onClick={() => { setShowAdjustForm(s => !s); setShowSuspendForm(false); setShowBonusForm(false); setShowDeleteForm(false); }}
                    className="w-full py-2.5 rounded-xl bg-white/5 text-white/60 text-sm font-bold border border-white/8 hover:bg-white/10 transition-colors">
                    Adjust Balance
                  </button>
                  {showAdjustForm && (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        {["credit", "debit"].map(t => (
                          <button key={t} onClick={() => setAdjustType(t)}
                            className={`flex-1 py-2 rounded-xl text-sm font-bold capitalize transition-all ${
                              adjustType === t
                                ? t === "credit" ? "bg-[#00C896]/20 text-[#00C896] border border-[#00C896]/30" : "bg-red-500/20 text-red-400 border border-red-500/30"
                                : "bg-white/5 text-white/30 border border-white/8"
                            }`}>{t === "credit" ? "+ Credit" : "− Debit"}</button>
                        ))}
                      </div>
                      <input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="Amount (₦)"
                        className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                      <input value={adjustNote} onChange={e => setAdjustNote(e.target.value)} placeholder="Reason / note (optional)"
                        className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                      <button onClick={handleAdjust} disabled={!adjustAmount || !!actionLoading}
                        className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40">
                        {actionLoading === "adjust" ? "Processing…" : `Confirm ${adjustType === "credit" ? "Credit" : "Debit"}`}
                      </button>
                    </div>
                  )}
                </div>

                {/* Reward bonus */}
                <div className="bg-white/4 rounded-xl border border-white/5 p-4 space-y-3">
                  <div>
                    <p className="text-white text-sm font-semibold">Grant Reward Bonus</p>
                    <p className="text-white/30 text-xs mt-0.5">Current: ₦{fmtAmt(rewards.rewardBalance)}</p>
                  </div>
                  <button onClick={() => { setShowBonusForm(s => !s); setShowSuspendForm(false); setShowAdjustForm(false); setShowDeleteForm(false); }}
                    className="w-full py-2.5 rounded-xl text-sm font-bold border transition-colors"
                    style={{ background: showBonusForm ? "rgba(0,200,150,0.15)" : "transparent", color: "#00C896", borderColor: "rgba(0,200,150,0.25)" }}>
                    <i className="ri-gift-line mr-1.5" />{showBonusForm ? "Cancel" : "Grant Bonus"}
                  </button>
                  {showBonusForm && (
                    <div className="space-y-3">
                      <input type="number" value={bonusAmount} onChange={e => setBonusAmount(e.target.value)} placeholder="Bonus amount (₦)" min={1}
                        className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                      <input value={bonusDesc} onChange={e => setBonusDesc(e.target.value)} placeholder="Description (e.g. Promo bonus, Compensation…)"
                        className="w-full bg-[#161616] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
                      <button onClick={handleBonus} disabled={!bonusAmount || Number(bonusAmount) < 1 || !!actionLoading}
                        className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                        {actionLoading === "bonus"
                          ? <><div className="w-3.5 h-3.5 border-2 border-black/20 border-t-black rounded-full animate-spin" />Crediting…</>
                          : <><i className="ri-gift-fill" />Credit ₦{bonusAmount ? Number(bonusAmount).toLocaleString() : "0"} Bonus</>
                        }
                      </button>
                    </div>
                  )}
                </div>

                {/* Danger zone — delete user */}
                <div className="bg-red-500/5 rounded-xl border border-red-500/15 p-4 space-y-3">
                  <div>
                    <p className="text-red-400 text-sm font-semibold flex items-center gap-1.5">
                      <i className="ri-error-warning-line" /> Danger Zone
                    </p>
                    <p className="text-white/30 text-xs mt-0.5">
                      Permanently deletes this account, wallet, KYC, and reward records. Transactions are kept for audit purposes.
                    </p>
                  </div>
                  <button onClick={() => { setShowDeleteForm(s => !s); setShowSuspendForm(false); setShowAdjustForm(false); setShowBonusForm(false); }}
                    className="w-full py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-bold border border-red-500/25 hover:bg-red-500/20 transition-colors">
                    {showDeleteForm ? "Cancel" : "Delete User"}
                  </button>
                  {showDeleteForm && (
                    <div className="space-y-2">
                      <p className="text-white/40 text-xs">
                        Type <span className="font-mono text-white/70">{user.userName}</span> to confirm.
                      </p>
                      <input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                        placeholder="Type username to confirm"
                        className="w-full bg-[#161616] border border-red-500/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-red-400/50" />
                      <button onClick={handleDelete} disabled={!confirmTextMatches || !!actionLoading}
                        className="w-full py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40">
                        {actionLoading === "delete" ? "Deleting…" : "Permanently Delete User"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Users View
// ─────────────────────────────────────────────────────────────
export default function UsersView() {
  const [users, setUsers]             = useState([]);
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [kycFilter, setKycFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy]           = useState("newest");
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]             = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const searchTimer = useRef(null);

  const fetchUsers = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await getAdminUsers({ page: p, limit: 20, search: search.trim(), kycStatus: kycFilter, status: statusFilter, sortBy });
      if (res.status) {
        setUsers(p === 1 ? res.data : prev => [...prev, ...res.data]);
        setStats(res.stats || {});
        setTotalPages(res.pagination.pages);
        setTotal(res.pagination.total);
        setPage(p);
      }
    } catch {}
    finally { setLoading(false); }
  }, [search, kycFilter, statusFilter, sortBy]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setUsers([]); fetchUsers(1); }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search, kycFilter, statusFilter, sortBy]);

  const KYC_FILTERS = [
    { value: "",            label: "All KYC" },
    { value: "approved",    label: "Approved" },
    { value: "submitted",   label: "Pending" },
    { value: "rejected",    label: "Rejected" },
    { value: "not_started", label: "No KYC" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">User Management</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {total.toLocaleString()} users ·{" "}
            {stats.pendingKyc > 0 && <span className="text-amber-400">{stats.pendingKyc} pending KYC · </span>}
            {stats.suspended  > 0 && <span className="text-red-400">{stats.suspended} suspended</span>}
          </p>
        </div>
        <button onClick={() => fetchUsers(1)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white transition-colors">
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by email, username, or user ID…"
            className="w-full bg-[#161616] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
        </div>
        <div className="flex gap-2">
          <select value={kycFilter} onChange={e => setKycFilter(e.target.value)}
            className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none focus:border-[#00C896]/40">
            {KYC_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none focus:border-[#00C896]/40">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none focus:border-[#00C896]/40">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden">
        {loading && users.length === 0 ? (
          <div className="divide-y divide-white/4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2"><div className="h-3 w-32 bg-white/5 rounded animate-pulse" /><div className="h-2.5 w-24 bg-white/5 rounded animate-pulse" /></div>
                <div className="h-6 w-20 bg-white/5 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mb-3">
              <i className="ri-user-line text-white/20 text-xl" />
            </div>
            <p className="text-white/30 text-sm">No users found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-white/4">
              {users.map(user => {
                const kycMeta = KYC_META[user.kycStatus || "not_started"];
                return (
                  <div key={user._id || user.userId} onClick={() => setSelectedUserId(user.userId)}
                    className="flex items-center gap-4 px-5 py-4 hover:bg-white/3 cursor-pointer transition-colors">
                    <div className="w-9 h-9 rounded-full bg-[#00C896]/10 flex items-center justify-center flex-shrink-0 relative">
                      <span className="text-[#00C896] text-xs font-bold">{initials(user.userName, user.email)}</span>
                      {user.suspended && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full flex items-center justify-center">
                          <i className="ri-forbid-line text-white text-[8px]" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{user.userName || "—"}</p>
                      <p className="text-white/30 text-xs truncate">{user.email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-white/20 text-[10px]">Joined {fmtDate(user.createdAt)}</p>
                        {user.lastLoginAt && <p className="text-white/20 text-[10px]">· Last login {fmtDate(user.lastLoginAt)}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Pill color={kycMeta?.color} bg={kycMeta?.bg}>{kycMeta?.label}</Pill>
                      <i className="ri-arrow-right-s-line text-white/20" />
                    </div>
                  </div>
                );
              })}
            </div>
            {page < totalPages && (
              <div className="px-5 py-3 border-t border-white/4">
                <button onClick={() => fetchUsers(page + 1)} disabled={loading}
                  className="w-full py-2.5 text-xs font-semibold text-[#00C896] hover:text-[#00b386] transition-colors disabled:opacity-50 text-center">
                  {loading ? "Loading…" : `Load more (${total - users.length} remaining)`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedUserId && (
        <UserDetailPanel
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onAction={() => fetchUsers(1)}
        />
      )}
    </div>
  );
}